'use client';

import { Share } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Button } from '@//components/Button';

const ShareButtonModal = lazy(() => import('./ShareButtonModal'));

interface ShareButtonProps {
  children?: React.ReactNode;
  renderButton?: (onClick: () => void) => React.ReactNode;
}

export const ShareButton: React.FC<ShareButtonProps> = ({ children, renderButton }) => {
  const t = useTranslations('components.shareButton');
  const [showModal, setShowModal] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  const onClick = () => {
    if (navigator.share) {
      navigator.share({ url: currentUrl }).catch(() => {});
    } else {
      setShowModal(true);
    }
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  const renderTrigger = () => {
    if (renderButton) {
      return renderButton(onClick);
    }

    if (children) {
      // Use event delegation instead of cloneElement to avoid hydration
      // mismatches when children are passed from server components (RSC)
      return (
        // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard events handled by the interactive child element
        <div onClick={onClick} style={{ display: 'contents' }}>
          {children}
        </div>
      );
    }

    return (
      <Button
        onClick={onClick}
        aria-label={t('button.title')}
        sizeLtMd="md"
        sizeMd="lg"
        variant="secondary"
      >
        <Share aria-hidden="true" />
        {t('button.label')}
      </Button>
    );
  };

  return (
    <>
      {renderTrigger()}
      <Suspense fallback={null}>
        <ShareButtonModal isOpen={showModal} currentUrl={currentUrl} onClose={onCloseModal} />
      </Suspense>
    </>
  );
};
